// src/pages/Admin/Communications/PushNotifications.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { 
  Bell, 
  Users, 
  Send, 
  Plus, 
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Trash2
} from 'lucide-react';
import { useToast } from '../../../hooks/useToast';
import unifiedAuth from '../../../utils/unifiedAuth';
import { config } from '../../../config';

interface CommunicationLog {
  id: string;
  subject: string;
  content: string;
  fromEmail: string;
  targetAudience: string;
  type: string;
  totalRecipients: number;
  successCount: number;
  failedCount: number;
  status: string;
  createdAt: string;
}

interface CommunicationStats {
  total_emails_sent: number;
  total_campaigns: number;
  total_subscribers: number;
  verified_subscribers: number;
}

const PushNotifications: React.FC = () => {
  const [communications, setCommunications] = useState<CommunicationLog[]>([]);
  const [stats, setStats] = useState<CommunicationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Filters and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Notification creation
  const [showNotificationModal, setShowNotificationModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [notificationForm, setNotificationForm] = useState({
    title: '',
    message: '',
    targetAudience: 'all' as string,
    fromEmail: 'hello'
  });
  
  const { showSuccess, showError } = useToast();

  // Email alias options
  const emailAliasOptions = [
    { value: 'hello', label: 'hello@strymhub.com - Welcome & Newsletter', description: 'Best for promotional content' },
    { value: 'admin', label: 'admin@strymhub.com - Admin Updates', description: 'For administrative announcements' },
    { value: 'contact', label: 'contact@strymhub.com - Contact Form', description: 'For general communications' },
    { value: 'support', label: 'support@strymhub.com - Support', description: 'For support-related updates' },
    { value: 'info', label: 'info@strymhub.com - Information', description: 'For general information emails' }
  ];

  const audienceOptions = [
    { value: 'all', label: 'All Users & Subscribers', description: 'All registered users + newsletter subscribers' },
    { value: 'active', label: 'Active Users', description: 'Registered users who logged in within 90 days' },
    { value: 'admins', label: 'Admin Users', description: 'Admin and staff users only' },
    { value: 'subscribers', label: 'Newsletter Subscribers Only', description: 'Only newsletter subscribers (not registered users)' }
  ];

  // Handle modal backdrop click
  const handleNotificationModalBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      setShowNotificationModal(false);
    }
  };

  // Lock body scroll when modal is open
  React.useEffect(() => {
    if (showNotificationModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [showNotificationModal]);

  const loadStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/email-notifications/communications/stats/`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        setStats({ total_emails_sent: 0, total_campaigns: 0, total_subscribers: 0, verified_subscribers: 0 });
      }
    } catch (error) {
      console.error('Failed to load stats:', error);
      setStats({ total_emails_sent: 0, total_campaigns: 0, total_subscribers: 0, verified_subscribers: 0 });
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const loadCommunications = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage.toString(),
        size: '20'
      });

      const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/email-notifications/communications/?${params}`, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCommunications(data.content || []);
        setTotalPages(data.totalPages || 1);
      } else {
        setCommunications([]);
      }
    } catch (error) {
      console.error('Failed to load communications:', error);
      setCommunications([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage]);

  // Send notification
  const sendNotification = async () => {
    if (!notificationForm.title.trim() || !notificationForm.message.trim()) {
      showError('Please fill in both title and message');
      return;
    }

    try {
      setSending(true);
      
      const params = new URLSearchParams({
        subject: notificationForm.title,
        content: notificationForm.message,
        fromEmail: notificationForm.fromEmail,
        targetAudience: notificationForm.targetAudience
      });

      const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/email-notifications/newsletter/send/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded'
        },
        body: params
      });

      const data = await response.json();

      if (response.ok) {
        const successCount = data.success_count || 0;
        const failedCount = data.failed_count || 0;
        const recipientCount = data.recipient_count || 0;
        
        if (recipientCount === 0) {
          showError('No recipients found for the selected audience.');
        } else if (failedCount > 0 && successCount > 0) {
          showSuccess(`Sent to ${successCount} of ${recipientCount} recipients (${failedCount} failed) from ${notificationForm.fromEmail}@strymhub.com`);
        } else {
          showSuccess(`Sent successfully to ${successCount} recipients from ${notificationForm.fromEmail}@strymhub.com`);
        }
        
        setNotificationForm({ title: '', message: '', targetAudience: 'all', fromEmail: 'hello' });
        setShowNotificationModal(false);
        loadCommunications();
        loadStats();
      } else {
        showError(data.message || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Failed to send notification:', error);
      showError('Failed to send notification. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const deleteCommunication = async (id: string) => {
    try {
      const response = await unifiedAuth.fetch(`${config.apiBaseUrl}/email-notifications/communications/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        showSuccess('Communication record deleted');
        loadCommunications();
        loadStats();
      } else {
        showError('Failed to delete communication record');
      }
    } catch (error) {
      console.error('Failed to delete communication:', error);
      showError('Failed to delete communication record');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  useEffect(() => {
    loadStats();
    loadCommunications();
  }, [loadStats, loadCommunications]);

  // Filter communications
  const filteredCommunications = communications.filter(comm => {
    const matchesSearch = !searchTerm || 
      comm.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
      comm.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || comm.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'SENT':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Sent</span>;
      case 'PARTIALLY_SENT':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Partial</span>;
      case 'FAILED':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Failed</span>;
      case 'SENDING':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Sending</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'SENT': return <CheckCircle className="h-5 w-5 text-green-600" />;
      case 'PARTIALLY_SENT': return <AlertTriangle className="h-5 w-5 text-yellow-600" />;
      case 'FAILED': return <XCircle className="h-5 w-5 text-red-600" />;
      default: return <Send className="h-5 w-5 text-blue-600" />;
    }
  };

  const getAudienceLabel = (audience: string) => {
    switch (audience) {
      case 'all': return 'All Users & Subscribers';
      case 'active': return 'Active Users';
      case 'admins': return 'Admin Users';
      case 'subscribers': return 'Newsletter Subscribers';
      default: return audience;
    }
  };

  return (
    <div className="p-3 sm:p-4 lg:p-6">
      <div className="mb-6 sm:mb-8">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">Push Notifications</h1>
        <p className="text-sm sm:text-base text-gray-600">Send targeted notifications and view communication history</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Send className="h-8 w-8 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Emails Sent</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '...' : stats?.total_emails_sent?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Bell className="h-8 w-8 text-orange-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Campaigns Sent</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '...' : stats?.total_campaigns?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <Users className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total Subscribers</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '...' : stats?.total_subscribers?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Verified Subscribers</p>
              <p className="text-2xl font-semibold text-gray-900">
                {statsLoading ? '...' : stats?.verified_subscribers?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-4 sm:mb-6">
        <button
          onClick={() => setShowNotificationModal(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Create Notification</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-4 border-b border-gray-200">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <input
                  type="text"
                  placeholder="Search communications..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="SENT">Sent</option>
                <option value="PARTIALLY_SENT">Partial</option>
                <option value="FAILED">Failed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Communication History List */}
        <div className="divide-y divide-gray-200">
          {loading ? (
            <div className="p-8 text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-500">Loading communications...</p>
            </div>
          ) : filteredCommunications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No communications found</h3>
              <p className="text-gray-500">Send your first notification to see it here.</p>
            </div>
          ) : (
            filteredCommunications.map((comm) => (
              <div key={comm.id} className="p-6 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="flex-shrink-0 mt-1">
                      {getStatusIcon(comm.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-medium text-gray-900 truncate">
                          {comm.subject}
                        </h3>
                        {getStatusBadge(comm.status)}
                      </div>
                      <p className="text-gray-700 mb-3 line-clamp-2">{comm.content}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500">
                        <span>Audience: {getAudienceLabel(comm.targetAudience)}</span>
                        <span>Recipients: {comm.totalRecipients}</span>
                        <span className="text-green-600">Success: {comm.successCount}</span>
                        {comm.failedCount > 0 && (
                          <span className="text-red-600">Failed: {comm.failedCount}</span>
                        )}
                        <span>From: {comm.fromEmail}@strymhub.com</span>
                        <span>Sent: {new Date(comm.createdAt).toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    <button 
                      onClick={() => setDeleteConfirmId(comm.id)}
                      className="p-2 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage <= 0}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Notification Creation Modal */}
      {showNotificationModal && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
          onClick={handleNotificationModalBackdropClick}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Create Push Notification</h2>
              <p className="text-sm text-gray-500 mt-1">Send a targeted notification email</p>
            </div>
            
            <div className="p-6 space-y-4">
              {/* From Email Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Send From
                </label>
                <select
                  value={notificationForm.fromEmail}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, fromEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {emailAliasOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {emailAliasOptions.find(o => o.value === notificationForm.fromEmail)?.description}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Title
                </label>
                <input
                  type="text"
                  value={notificationForm.title}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="Enter notification title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message
                </label>
                <textarea
                  value={notificationForm.message}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, message: e.target.value }))}
                  placeholder="Write your notification message here..."
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience
                </label>
                <select
                  value={notificationForm.targetAudience}
                  onChange={(e) => setNotificationForm(prev => ({ ...prev, targetAudience: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {audienceOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  {audienceOptions.find(o => o.value === notificationForm.targetAudience)?.description}
                </p>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>Recipients:</strong> This notification will be sent to{' '}
                  {notificationForm.targetAudience === 'all' ? 'all registered users and newsletter subscribers' :
                   notificationForm.targetAudience === 'active' ? 'recently active registered users (logged in within 90 days)' :
                   notificationForm.targetAudience === 'admins' ? 'admin and staff users only' :
                   notificationForm.targetAudience === 'subscribers' ? 'newsletter subscribers only' :
                   'the selected audience'}.
                </p>
              </div>
              
              {/* Email Preview Note */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Email Template Preview</h4>
                <div className="text-xs text-gray-600 space-y-1">
                  <p>Professional header with StrymHub branding</p>
                  <p>Personalized greeting with recipient's name</p>
                  <p>Your title as the main heading</p>
                  <p>Your message formatted for email</p>
                  <p>Footer with unsubscribe link</p>
                </div>
              </div>
            </div>
            
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => setShowNotificationModal(false)}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={sending}
              >
                Cancel
              </button>
              <button
                onClick={sendNotification}
                disabled={sending}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="h-4 w-4" />
                <span>{sending ? 'Sending...' : 'Send Now'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmId && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[9999]"
          onClick={(e) => { if (e.target === e.currentTarget) setDeleteConfirmId(null); }}
          style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0 w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">Delete Communication</h3>
              </div>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete this communication record? This action cannot be undone.
              </p>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setDeleteConfirmId(null)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => deleteCommunication(deleteConfirmId)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PushNotifications;